import type { InferRpcDefinition } from "@redotech/rpc/definition";
import { createContextLibraryItem } from "./schema/ai-context-library/create-context-library-item";
import { deleteContextLibraryItem } from "./schema/ai-context-library/delete-context-library-item";
import { getContextLibraryItemById } from "./schema/ai-context-library/get-context-library-item-by-id";
import { getContextLibraryItems } from "./schema/ai-context-library/get-context-library-items";
import { getContextLibraryItemsByScenario } from "./schema/ai-context-library/get-context-library-items-by-scenario";
import { updateContextLibraryItem } from "./schema/ai-context-library/update-context-library-item";
import { analyzeSampleCustomers } from "./schema/ai/analyze-customers/analyze-sample-customers";
import {
  getAiAnalyzedFileContent,
  getAiAnalyzedFiles,
} from "./schema/ai/get-ai-analyzed-files";
import { paginateAiAnalyzedFiles } from "./schema/ai/paginate-ai-analyzed-files";
import { sendMessage } from "./schema/ai/send-message";
import { getAudience } from "./schema/audience/get-audience";
import { setCustomerSubscribed } from "./schema/audience/set-customer-subscribed";
import { setCustomerSuppressed } from "./schema/audience/set-customer-suppressed";
import { deleteAutomation } from "./schema/automations/delete-automation";
import { getTeamAutomations } from "./schema/automations/get-team-automations";
import { revertAutomationToOrigin } from "./schema/automations/revert-automation-to-origin";
import { setAutomationEnabled } from "./schema/automations/set-automation-enabled";
import { updateAutomation } from "./schema/automations/update-automation";
import { getBackInStockProducts } from "./schema/back-in-stock/get-back-in-stock-products";
import { getBackInStockSubscribers } from "./schema/back-in-stock/get-back-in-stock-subscribers";
import { generatePdfForInvoice } from "./schema/billing/generate-pdf-for-invoice";
import { getCurrentMarketingEmailInvoice } from "./schema/billing/get-current-marketing-email-invoice";
import { getCurrentMarketingSmsInvoice } from "./schema/billing/get-current-marketing-sms-invoice";
import { getEstimatedEmailCost } from "./schema/billing/get-estimated-email-cost";
import { getEstimatedSmsCost } from "./schema/billing/get-estimated-sms-cost";
import { getFinalizedServiceInvoices } from "./schema/billing/get-finalized-service-invoices";
import { getCurrentOrderTrackingInvoice } from "./schema/billing/order-tracking/get-current-order-tracking-invoice";
import { getFinalizedOrderTrackingInvoices } from "./schema/billing/order-tracking/get-finalized-order-tracking-invoices";
import { addABTestVariant } from "./schema/campaigns/add-ab-test-variant";
import {
  getCampaignRecipientsCount,
  paginateCampaignRecipients,
} from "./schema/campaigns/campaign-recipient";
import { createCampaign } from "./schema/campaigns/create-campaign";
import { deleteCampaign } from "./schema/campaigns/delete-campaign";
import { duplicateCampaign } from "./schema/campaigns/duplicate-campaign";
import { getCampaignById } from "./schema/campaigns/get-campaign-by-id";
import { getCampaignRecipientsByOrderId } from "./schema/campaigns/get-campaign-recipients-by-order-id";
import { getCustomerGroups } from "./schema/campaigns/get-customer-groups";
import { getCustomerGroupsMinimal } from "./schema/campaigns/get-customer-groups-minimal";
import { getTeamCampaigns } from "./schema/campaigns/get-team-campaigns";
import { removeABTestVariant } from "./schema/campaigns/remove-ab-test-variant";
import { updateCampaign } from "./schema/campaigns/update-campaign";
import { getConversationById } from "./schema/conversations/get-conversation-by-id";
import { getConversations } from "./schema/conversations/get-conversations";
import { getQueueConversations } from "./schema/conversations/get-queue-conversations";
import { getQueueCount } from "./schema/conversations/get-queue-count";
import { createCustomDomain } from "./schema/custom-domains/create-custom-domain";
import { deleteCustomDomain } from "./schema/custom-domains/delete-custom-domain";
import { getCustomDomain } from "./schema/custom-domains/get-custom-domain";
import { getDomainWarmingReputationMetrics } from "./schema/custom-domains/get-domain-warming-reputation-metrics";
import { getPublicKey } from "./schema/custom-domains/get-public-key";
import { setCustomDomainMailFromDomain } from "./schema/custom-domains/set-custom-domain-mail-from-domain";
import { updateCustomDomainEmailSettings } from "./schema/custom-domains/update-custom-domain-email-settings";
import { getCustomEventNames } from "./schema/custom-events/get-custom-event-names";
import { createCustomField } from "./schema/customer-custom-fields/create-custom-field";
import { deleteCustomField } from "./schema/customer-custom-fields/delete-custom-field";
import { getCustomFieldOptions } from "./schema/customer-custom-fields/get-custom-field-options";
import { getCustomFields } from "./schema/customer-custom-fields/get-custom-fields";
import { getCustomerPersonas } from "./schema/customers/get-customer-personas";
import { getCustomersCount } from "./schema/customers/get-customers-count";
import { getSegmentsForCustomer } from "./schema/customers/get-segments-for-customer";
import { paginateCustomersWithPersonas } from "./schema/customers/paginate-customers-with-personas";
import { createDiscount } from "./schema/discounts/create-discount";
import { deleteDiscount } from "./schema/discounts/delete-discount";
import { generatePreviewDiscountCode } from "./schema/discounts/generate-preview-discount-code";
import { getDiscount } from "./schema/discounts/get-discount";
import { getDiscountsByTeam } from "./schema/discounts/get-discounts-by-team";
import { updateDiscount } from "./schema/discounts/update-discount";
import { emailTemplateSchema } from "./schema/email-templates/index";
import { getBooleanFeatureFlag } from "./schema/feature-flags/get-boolean-feature-flag";
import { generateDiscountCode } from "./schema/games/generate-discount-code";
import { getCities } from "./schema/geographic-units/get-cities";
import { getCountries } from "./schema/geographic-units/get-countries";
import { getStates } from "./schema/geographic-units/get-states";
import { getAudienceGrowthAnalytics } from "./schema/get-audience-growth-analytics";
import { getAutomationPerformanceAnalytics } from "./schema/get-automation-performance-analytics";
import { getCampaignPerformanceAnalytics } from "./schema/get-campaign-performance-analytics";
import { getCustomerSuppressionInfo } from "./schema/get-customer-suppression-info";
import { getDailyRevenueMetrics } from "./schema/get-daily-revenue-metrics";
import { getMessagePerformanceMetrics } from "./schema/get-message-performance-metrics";
import { getMonthlyDeliverabilityMetrics } from "./schema/get-monthly-deliverability-metrics";
import { getOutreachAnalytics } from "./schema/get-outreach-analytics";
import { getSubscriptionProducts } from "./schema/get-subscription-products";
import { getTeamPhoneNumber } from "./schema/get-team-phone-number-schema";
import { syncHistoricalReviews } from "./schema/importers/sync-historical-reviews";
import { createGoogleAdsUserList } from "./schema/integrations/create-google-ads-user-list";
import { createMetaAdsAudience } from "./schema/integrations/create-meta-ads-audience";
import { createYotpoIntegration } from "./schema/integrations/create-yotpo-integration";
import { getAttentiveIntegration } from "./schema/integrations/get-attentive-integration";
import { getGoogleAdsCustomers } from "./schema/integrations/get-google-ads-customers";
import { getGoogleAdsIntegration } from "./schema/integrations/get-google-ads-integration";
import { getGoogleAdsOAuthURL } from "./schema/integrations/get-google-ads-oauth-url";
import { getGoogleAdsUserLists } from "./schema/integrations/get-google-ads-user-lists";
import { getGrowaveIntegration } from "./schema/integrations/get-growave-integration";
import { getMetaAdsAccounts } from "./schema/integrations/get-meta-ads-accounts";
import { getMetaAdsAudiences } from "./schema/integrations/get-meta-ads-audiences";
import { getMetaAdsIntegration } from "./schema/integrations/get-meta-ads-integration";
import { getMetaAdsOAuthURL } from "./schema/integrations/get-meta-ads-oauth-url";
import { getPostscriptIntegration } from "./schema/integrations/get-postscript-integration";
import { getRechargeIntegration } from "./schema/integrations/get-recharge-integration";
import { getSkioIntegration } from "./schema/integrations/get-skio-integration";
import { getYotpoIntegration } from "./schema/integrations/get-yotpo-integration";
import { saveConnectionsToGoogleAds } from "./schema/integrations/save-connections-to-google-ads";
import { saveConnectionsToMetaAds } from "./schema/integrations/save-connections-to-meta-ads";
import { searchCollections } from "./schema/integrations/search-collections";
import { subscribeToPostscriptWebhook } from "./schema/integrations/subscribe-to-postscript-webhook";
import { unsubscribeFromPostscriptWebhook } from "./schema/integrations/unsubscribe-from-postscript-webhook";
import { updateYotpoIntegration } from "./schema/integrations/update-yotpo-integration";
import { upsertGrowaveIntegration } from "./schema/integrations/upsert-growave-integration";
import { upsertPostscriptIntegration } from "./schema/integrations/upsert-postscript-integration";
import { upsertRechargeIntegration } from "./schema/integrations/upsert-recharge-integration";
import { upsertSkioIntegration } from "./schema/integrations/upsert-skio-integration";
import { examplePopulatedLiquidTemplate } from "./schema/liquid/example-populated-liquid-template";
import { createLoyaltyPromotion } from "./schema/loyalty/create-loyalty-promotion";
import { deleteLoyaltyPromotion } from "./schema/loyalty/delete-loyalty-promotion";
import { getLoyaltyConfiguration } from "./schema/loyalty/get-loyalty-configuration";
import { getLoyaltyMembers } from "./schema/loyalty/get-loyalty-members";
import { getLoyaltyMetrics } from "./schema/loyalty/get-loyalty-metrics";
import { getLoyaltyPopupSettings } from "./schema/loyalty/get-loyalty-popup-settings";
import { getLoyaltyPromotions } from "./schema/loyalty/get-loyalty-promotions";
import { getLoyaltyRefundEventForReturn } from "./schema/loyalty/get-loyalty-refund-event-for-return";
import { getLoyaltyPointsEarnedFromOrder } from "./schema/loyalty/get-points-earned-from-order";
import { updateLoyaltyPageSettings } from "./schema/loyalty/update-loyalty-page";
import { updateLoyaltyPopupSettings } from "./schema/loyalty/update-loyalty-popup-settings";
import { updateLoyaltyPromotion } from "./schema/loyalty/update-loyalty-promotion";
import { upsertLoyaltyConfiguration } from "./schema/loyalty/upsert-loyalty-configuration";
import { metaLeadAds } from "./schema/meta-lead-ads/index";
import { orderTrackingAnalyticsSchema } from "./schema/order-tracking-analytics";
import { getProvidersForTeam } from "./schema/order-tracking/get-providers-for-team";
import { getRecentOrders } from "./schema/order-tracking/get-recent-orders";
import { getTrackingPageSettings } from "./schema/order-tracking/get-tracking-page-settings";
import { updateTrackingPageSettings } from "./schema/order-tracking/update-tracking-page-settings";
import { getPersonaAnalytics } from "./schema/personas/get-persona-analytics";
import { createProductFilter } from "./schema/product-filters/create-product-filter";
import { deleteProductFilter } from "./schema/product-filters/delete-product-filter";
import { getCollectionById } from "./schema/product-filters/get-collection-by-id";
import { getProductFilterById } from "./schema/product-filters/get-product-filter-by-id";
import { getProductFiltersByTeam } from "./schema/product-filters/get-product-filters-by-team";
import { updateProductFilter } from "./schema/product-filters/update-product-filter";
import { getProductImages } from "./schema/product-images/get-product-images";
import { getEmailInteractiveProductRecommendations } from "./schema/product-recommendations/get-email-interactive-product-recommendations";
import { getEmailStaticVariantRecommendations } from "./schema/product-recommendations/get-email-static-variant-recommendations";
import { getRecommendedProductsByProvider } from "./schema/product-recommendations/get-recommended-products-by-provider";
import { getTeamProviders } from "./schema/providers/get-team-providers";
import { archiveSavedEmailSection } from "./schema/saved-email-sections/archive-saved-email-section";
import { createSavedEmailSection } from "./schema/saved-email-sections/create-saved-email-section";
import { getSavedEmailSectionById } from "./schema/saved-email-sections/get-saved-email-section-by-id";
import { getSavedEmailSections } from "./schema/saved-email-sections/get-saved-email-sections";
import { updateSavedEmailSection } from "./schema/saved-email-sections/update-saved-email-section";
import { createSavedEmailTemplate } from "./schema/saved-templates/create-saved-email-template";
import { deleteSavedEmailTemplate } from "./schema/saved-templates/delete-saved-email-template";
import { getSavedEmailTemplateById } from "./schema/saved-templates/get-saved-email-template-by-id";
import { getSavedEmailTemplates } from "./schema/saved-templates/get-saved-email-templates";
import { getUpdatedSavedEmailTemplates } from "./schema/saved-templates/get-updated-saved-email-templates";
import { paginateSavedEmailTemplates } from "./schema/saved-templates/paginate-saved-email-templates";
import { updateSavedEmailTemplate } from "./schema/saved-templates/update-saved-email-template";
import { createDynamicSegment } from "./schema/segments/create-dynamic-segment";
import { createSampledSegment } from "./schema/segments/create-sampled-segment";
import { createStaticSegment } from "./schema/segments/create-static-segment";
import { deleteSegment } from "./schema/segments/delete-segment";
import { deleteSegments } from "./schema/segments/delete-segments";
import { fetchSegmentById } from "./schema/segments/fetch-segment-by-id";
import { fetchTeamSegments } from "./schema/segments/fetch-team-segments";
import { getEstimatedCampaignRecipientCount } from "./schema/segments/get-estimated-campaign-recipient-count";
import { getOrCreateTexasSegmentForTeam } from "./schema/segments/get-or-create-texas-segment-for-team";
import { getSegmentCollections } from "./schema/segments/get-segment-collections";
import { getSegmentCount } from "./schema/segments/get-segment-count";
import { getSegmentMembers } from "./schema/segments/get-segment-members";
import { getSegmentProductVendors } from "./schema/segments/get-segment-product-vendors";
import { getSegmentProducts } from "./schema/segments/get-segment-products";
import { getShopifySegments } from "./schema/segments/get-shopify-segments";
import { getTokenFieldOptions } from "./schema/segments/get-token-field-options";
import { refreshSegmentCount } from "./schema/segments/refresh-segment-count";
import { updateDynamicSegment } from "./schema/segments/update-dynamic-segment";
import { updateStaticSegment } from "./schema/segments/update-static-segment";
import { createSignupForm } from "./schema/signup-form/create-signup-form";
import { createSignupFormSplit } from "./schema/signup-form/create-signup-form-split";
import { deleteSignupForm } from "./schema/signup-form/delete-signup-form";
import { duplicateSignupForm } from "./schema/signup-form/duplicate-signup-form";
import { getMarketingSubscriberCounts } from "./schema/signup-form/get-marketing-subscriber-counts";
import { getSignupFormById } from "./schema/signup-form/get-signup-form-by-id";
import { getSignupFormSplits } from "./schema/signup-form/get-signup-form-splits";
import { getSignupFormsByTeamId } from "./schema/signup-form/get-signup-forms-by-team-id";
import { unpublishSignupForm } from "./schema/signup-form/unpublish-signup-form";
import { updatePublishedAt } from "./schema/signup-form/update-published-at";
import { updateSignupForm } from "./schema/signup-form/update-signup-form";
import { updateSignupFormSplitStatus } from "./schema/signup-form/update-signup-form-split-status";
import { verifyOrCreateNotOnSaleCollection } from "./schema/signup-form/verify-or-create-not-on-sale-collection";
import { createSmsSignupTemplates } from "./schema/sms-template/create-sms-signup-template";
import { createSmsTemplate } from "./schema/sms-template/create-sms-template";
import { deleteSmsTemplate } from "./schema/sms-template/delete-sms-template";
import { createContentGeneratorConfig } from "./schema/sms-templates/create-content-generator-config";
import { generateSmsAiContentPreview } from "./schema/sms-templates/generate-sms-ai-content-preview";
import { getSmsTemplateAiOptions } from "./schema/sms-templates/get-sms-template-ai-options";
import { getSmsTemplateById } from "./schema/sms-templates/get-sms-template-by-id";
import { getSmsTemplateByIdPopulated } from "./schema/sms-templates/get-sms-template-by-id-populated";
import { paginateSmsTemplates } from "./schema/sms-templates/paginate-sms-templates";
import { publishContentGeneratorConfig } from "./schema/sms-templates/publish-content-generator-config";
import { sendTestSMSById } from "./schema/sms-templates/send-test-sms-by-id";
import { updateSmsTemplate } from "./schema/sms-templates/update-sms-template";
import { speechToText } from "./schema/speech-to-text/speech-to-text";
import { setPreferredSupportEmail } from "./schema/support-connection/set-preferred-support-email";
import { createTrackingPage } from "./schema/tracking-page-flow/create-tracking-page";
import { getTrackingPageById } from "./schema/tracking-page-flow/get-tracking-page-by-id";
import { getTrackingPageFlowSteps } from "./schema/tracking-page-flow/get-tracking-page-flow-steps";
import { getTrackingPages } from "./schema/tracking-page-flow/get-tracking-pages";
import { updateTrackingPageById } from "./schema/tracking-page-flow/update-tracking-page-by-id";
import { updateTrackingPageFlow } from "./schema/tracking-page-flow/update-tracking-page-flow";

const aiSchema = {
  getAiAnalyzedFileContent,
  getAiAnalyzedFiles,
  paginateAiAnalyzedFiles,
  generateSmsAiContentPreview,
  sendMessage,
  analyzeSampleCustomers,
};

const aiContextLibrarySchema = {
  getContextLibraryItems,
  getContextLibraryItemsByScenario,
  createContextLibraryItem,
  getContextLibraryItemById,
  updateContextLibraryItem,
  deleteContextLibraryItem,
};

const audienceSchema = {
  getAudience,
  setCustomerSubscribed,
  setCustomerSuppressed,
};

const automationsSchema = {
  getTeamAutomations,
  setAutomationEnabled,
  updateAutomation,
  deleteAutomation,
  revertAutomationToOrigin,
  examplePopulatedLiquidTemplate,
  getCustomEventNames,
};

const customersSchema = {
  paginateCustomersWithPersonas,
  getCustomersCount,
  getCustomerPersonas,
  getSegmentsForCustomer,
};

const billingSchema = {
  generatePdfForInvoice,
  getCurrentMarketingEmailInvoice,
  getCurrentMarketingSmsInvoice,
  getEstimatedEmailCost,
  getEstimatedSmsCost,
  getFinalizedServiceInvoices,
  getCurrentOrderTrackingInvoice,
  getFinalizedOrderTrackingInvoices,
};

const campaignSchema = {
  createCampaign,
  deleteCampaign,
  duplicateCampaign,
  getCampaignById,
  getCampaignRecipientsCount,
  getCampaignRecipientsByOrderId,
  getTeamCampaigns,
  paginateCampaignRecipients,
  updateCampaign,
  addABTestVariant,
  removeABTestVariant,
};

const customFieldsSchema = {
  createCustomField,
  deleteCustomField,
  getCustomFields,
  getCustomFieldOptions,
};

const discountSchema = {
  createDiscount,
  deleteDiscount,
  generatePreviewDiscountCode,
  getDiscount,
  getDiscountsByTeam,
  updateDiscount,
};

const geographicSchema = { getCities, getCountries, getStates };

const integrationSchema = {
  createGoogleAdsUserList,
  createMetaAdsAudience,
  createYotpoIntegration,
  getAttentiveIntegration,
  getGoogleAdsCustomers,
  getGoogleAdsIntegration,
  getGoogleAdsOAuthURL,
  getGoogleAdsUserLists,
  getGrowaveIntegration,
  upsertGrowaveIntegration,
  getMetaAdsAccounts,
  getMetaAdsAudiences,
  getMetaAdsIntegration,
  getMetaAdsOAuthURL,
  getPostscriptIntegration,
  subscribeToPostscriptWebhook,
  unsubscribeFromPostscriptWebhook,
  upsertPostscriptIntegration,
  getRechargeIntegration,
  upsertRechargeIntegration,
  getSkioIntegration,
  upsertSkioIntegration,
  getYotpoIntegration,
  updateYotpoIntegration,
  saveConnectionsToGoogleAds,
  saveConnectionsToMetaAds,
  searchCollections,
  ...metaLeadAds,
};

const orderTrackingSchema = {
  getProvidersForTeam,
  getRecentOrders,
  getTrackingPageSettings,
  updateTrackingPageSettings,
};

const trackingPageFlowSchema = {
  createTrackingPage,
  getTrackingPageById,
  getTrackingPageFlowSteps,
  getTrackingPages,
  updateTrackingPageById,
  updateTrackingPageFlow,
};

const productFilterSchema = {
  createProductFilter,
  deleteProductFilter,
  getCollectionById,
  getProductFilterById,
  getProductFiltersByTeam,
  updateProductFilter,
};

const savedEmailTemplateSchema = {
  createSavedEmailTemplate,
  getSavedEmailTemplateById,
  getSavedEmailTemplates,
  getUpdatedSavedEmailTemplates,
  paginateSavedEmailTemplates,
  updateSavedEmailTemplate,
  deleteSavedEmailTemplate,
};

const segmentSchema = {
  createDynamicSegment,
  createStaticSegment,
  deleteSegment,
  deleteSegments,
  fetchSegmentById,
  fetchTeamSegments,
  getEstimatedCampaignRecipientCount,
  getOrCreateTexasSegmentForTeam,
  getSegmentCollections,
  getSegmentCount,
  getSegmentMembers,
  getSegmentProducts,
  getSegmentProductVendors,
  getShopifySegments,
  getTokenFieldOptions,
  refreshSegmentCount,
  updateDynamicSegment,
  updateStaticSegment,
  createSampledSegment,
};

const signupFormSchema = {
  createSignupForm,
  createSignupFormSplit,
  deleteSignupForm,
  getMarketingSubscriberCounts,
  getSignupFormById,
  getSignupFormsByTeamId,
  getSignupFormSplits,
  unpublishSignupForm,
  updatePublishedAt,
  updateSignupForm,
  updateSignupFormSplitStatus,
  verifyOrCreateNotOnSaleCollection,
  duplicateSignupForm,
};

const customDomainSchema = {
  getCustomDomain,
  createCustomDomain,
  setCustomDomainMailFromDomain,
  updateCustomDomainEmailSettings,
  deleteCustomDomain,
  getPublicKey,
  getDomainWarmingReputationMetrics,
};

const loyaltySchema = {
  getLoyaltyConfiguration,
  getLoyaltyMembers,
  getLoyaltyMetrics,
  getLoyaltyPopupSettings,
  upsertLoyaltyConfiguration,
  getLoyaltyPromotions,
  createLoyaltyPromotion,
  updateLoyaltyPromotion,
  deleteLoyaltyPromotion,
  getLoyaltyRefundEventForReturn,
  getLoyaltyPointsEarnedFromOrder,
  updateLoyaltyPageSettings,
  updateLoyaltyPopupSettings,
};

const analyticsSchema = {
  getAudienceGrowthAnalytics,
  getAutomationPerformanceAnalytics,
  getCampaignPerformanceAnalytics,
  getDailyRevenueMetrics,
  getCustomerSuppressionInfo,
  getMessagePerformanceMetrics,
  getMonthlyDeliverabilityMetrics,
  getOutreachAnalytics,
};

const smsSchema = {
  deleteSmsTemplate,
  createSmsTemplate,
  createContentGeneratorConfig,
  getSmsTemplateById,
  getSmsTemplateByIdPopulated,
  getSmsTemplateAiOptions,
  paginateSmsTemplates,
  publishContentGeneratorConfig,
  sendTestSMSById,
  updateSmsTemplate,
  createSmsSignupTemplates,
  getTeamPhoneNumber,
};

const recommendationSchema = {
  getEmailStaticVariantRecommendations,
  getEmailInteractiveProductRecommendations,
  getRecommendedProductsByProvider,
};

const providersSchema = { getTeamProviders };

const savedEmailSectionsSchema = {
  getSavedEmailSections,
  getSavedEmailSectionById,
  createSavedEmailSection,
  updateSavedEmailSection,
  archiveSavedEmailSection,
};

const importersSchema = { syncHistoricalReviews };

const conversationsSchema = {
  getConversations,
  getConversationById,
  getQueueConversations,
  getQueueCount,
};

export const rpcDefinition = {
  // ai
  ...aiSchema,
  // ai context library
  ...aiContextLibrarySchema,
  // audience
  ...audienceSchema,
  // customers
  ...customersSchema,
  // personas
  getPersonaAnalytics,
  // automations
  ...automationsSchema,
  // billing
  ...billingSchema,
  // campaign
  ...campaignSchema,
  // customer custom fields
  ...customFieldsSchema,
  // discount
  ...discountSchema,
  // email template
  ...emailTemplateSchema,
  // feature flags
  getBooleanFeatureFlag,
  // geographic unit
  ...geographicSchema,
  // integration
  ...integrationSchema,
  // importers
  ...importersSchema,
  // order tracking
  ...orderTrackingSchema,
  // tracking page flow
  ...trackingPageFlowSchema,
  // order tracking analytics
  ...orderTrackingAnalyticsSchema,
  // product filter
  ...productFilterSchema,
  // product images
  getProductImages,
  // subscription products
  getSubscriptionProducts,
  // saved email template
  ...savedEmailTemplateSchema,
  // segment
  ...segmentSchema,
  // signup form
  ...signupFormSchema,
  // support
  setPreferredSupportEmail,
  // custom domains
  ...customDomainSchema,
  // analytics
  ...analyticsSchema,
  // sms
  ...smsSchema,
  // recommendations
  ...recommendationSchema,
  // providers
  ...providersSchema,
  // others
  getCustomerGroups,
  getCustomerGroupsMinimal,
  // loyalty
  ...loyaltySchema,
  // tracking page
  generateDiscountCode,
  // back in stock
  getBackInStockProducts,
  getBackInStockSubscribers,
  // speech-to-text
  speechToText,
  // saved email sections
  ...savedEmailSectionsSchema,
  // conversations
  ...conversationsSchema,
};

export type RedoMarketingRpcDefinition = InferRpcDefinition<
  typeof rpcDefinition
>;
